import { useEffect, useState } from "react"
import type { FormAnswer, FormRowDefinition } from "@/types/agent"

import { TextInput } from "../ui/text-input"

interface QuestionFormInputProps {
  value: FormAnswer | undefined
  onChange: (value: FormAnswer) => void
  disabled?: boolean
  rows: FormRowDefinition[]
}

function getFieldDefaults(rows: FormRowDefinition[]): FormAnswer {
  const defaults: FormAnswer = {}
  for (const row of rows) {
    for (const field of row.fields) {
      if (field.default !== undefined) {
        defaults[field.id] = field.default
      }
    }
  }
  return defaults
}

export function QuestionFormInput({
  value,
  onChange,
  disabled,
  rows,
}: QuestionFormInputProps) {
  const [fieldValues, setFieldValues] = useState<FormAnswer>(() => ({
    ...getFieldDefaults(rows),
    ...value,
  }))

  useEffect(() => {
    onChange(fieldValues)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValues])

  const updateField = (id: string, newValue: string) => {
    setFieldValues((prev) => ({
      ...prev,
      [id]: newValue,
    }))
  }

  if (rows.length === 0) {
    return <div className="text-sm text-gray-10">No fields configured</div>
  }

  return (
    <div className="flex max-w-md flex-col gap-4">
      {rows.map((row, rowIdx) => (
        <div key={rowIdx} className="flex gap-2.5">
          {row.fields.map((field) => (
            <div key={field.id} className="min-w-0 flex-1">
              {/* Placeholder-only (no visible label) for a minimal look; the label
                  still names the field for assistive tech. */}
              <TextInput
                size="sm"
                value={fieldValues[field.id] ?? ""}
                placeholder={field.placeholder ?? field.label}
                aria-label={field.label}
                onChange={(e) => updateField(field.id, e.target.value)}
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}
